import { useCallback, useMemo, useRef } from 'react';
import useDebouncedCommit from './useDebouncedCommit';
import { getMixedValue, getSelectionType, getSelectedElements, getLockedState } from '../utils/selectionUtils';
import { applyElementPatch } from '../utils/elementPatch';
import { sanitizeDeep } from '../utils/sanitize';
import { measureTextBox } from '../utils/textMeasure';

const TEXT_AFFECTING_FIELDS = new Set([
  'text',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'lineHeight',
  'letterSpacing',
  'paddingX',
  'paddingY',
  'align',
  'autoWidth',
  'autoHeight',
]);

const shouldAutoFit = (prevEl, nextEl) => {
  if (nextEl.type !== 'text') return false;
  const autoWidth = nextEl.autoWidth !== false;
  const autoHeight = nextEl.autoHeight !== false;
  if (!autoWidth && !autoHeight) return false;
  return Array.from(TEXT_AFFECTING_FIELDS).some((key) => prevEl[key] !== nextEl[key]);
};

const adjustTextBounds = (element) => {
  const autoWidth = element.autoWidth !== false;
  const autoHeight = element.autoHeight !== false;
  if (!autoWidth && !autoHeight) return element;
  const measure = measureTextBox({
    text: element.text,
    wrapWidth: autoWidth ? undefined : element.w,
    autoWidth,
    fontSize: element.fontSize,
    fontFamily: element.fontFamily,
    fontWeight: element.fontWeight,
    fontStyle: element.fontStyle,
    lineHeight: element.lineHeight,
    letterSpacing: element.letterSpacing,
    paddingX: element.paddingX,
    paddingY: element.paddingY,
    minW: element.minW || 40,
  });
  return {
    ...element,
    w: autoWidth ? measure.tightW : element.w,
    h: autoHeight ? measure.tightH : element.h,
  };
};

const usePropertiesModel = ({
  elements,
  selectedIds,
  setElementsLocal,
  commitElementsToFirestore,
  isInteractingRef,
}) => {
  const selectedElements = useMemo(
    () => getSelectedElements(elements, selectedIds),
    [elements, selectedIds],
  );
  const selectionType = useMemo(
    () => getSelectionType(selectedElements),
    [selectedElements],
  );
  const lockedState = useMemo(
    () => getLockedState(selectedElements),
    [selectedElements],
  );
  const canEdit = !lockedState.allLocked && selectedElements.length > 0;

  const debounced = useDebouncedCommit(commitElementsToFirestore, 800);
  const pendingCommitRef = useRef(null);
  const commitOnReleaseTimerRef = useRef(null);

  const mixed = useCallback(
    (path) => getMixedValue(selectedElements, path),
    [selectedElements],
  );

  const applyToSelection = useCallback((patchOrUpdater, commitMode = 'debounced', reason) => {
    if (!selectedElements.length) return;
    const nextElements = elements.map((el) => {
      if (!selectedIds.includes(el.id)) return el;
      const patch = typeof patchOrUpdater === 'function'
        ? patchOrUpdater(el)
        : patchOrUpdater;
      if (!patch) return el;
      const allowsLocked = Object.prototype.hasOwnProperty.call(patch, 'locked');
      if (el.locked && !allowsLocked) return el;
      let nextEl = applyElementPatch(el, patch);
      if (shouldAutoFit(el, nextEl)) {
        nextEl = adjustTextBounds(nextEl);
      }
      return nextEl;
    });

    setElementsLocal(nextElements);

    if (commitMode === 'immediate') {
      commitElementsToFirestore(sanitizeDeep(nextElements), reason);
      return;
    }

    if (commitMode === 'debounced') {
      debounced.schedule(sanitizeDeep(nextElements), reason);
      return;
    }

    if (commitMode === 'commitOnRelease') {
      pendingCommitRef.current = { elements: sanitizeDeep(nextElements), reason };
      if (!commitOnReleaseTimerRef.current) {
        commitOnReleaseTimerRef.current = setTimeout(() => {
          if (pendingCommitRef.current) {
            commitElementsToFirestore(
              pendingCommitRef.current.elements,
              pendingCommitRef.current.reason || reason,
            );
            pendingCommitRef.current = null;
          }
          commitOnReleaseTimerRef.current = null;
        }, 800);
      }
    }
  }, [
    commitElementsToFirestore,
    debounced,
    elements,
    selectedElements.length,
    selectedIds,
    setElementsLocal,
  ]);

  const commitPending = useCallback((reason) => {
    if (!pendingCommitRef.current) return;
    const payload = pendingCommitRef.current;
    pendingCommitRef.current = null;
    if (commitOnReleaseTimerRef.current) {
      clearTimeout(commitOnReleaseTimerRef.current);
      commitOnReleaseTimerRef.current = null;
    }
    commitElementsToFirestore(payload.elements, reason || payload.reason);
  }, [commitElementsToFirestore]);

  const cancelPending = useCallback(() => {
    pendingCommitRef.current = null;
    if (commitOnReleaseTimerRef.current) {
      clearTimeout(commitOnReleaseTimerRef.current);
      commitOnReleaseTimerRef.current = null;
    }
    debounced.cancel();
  }, [debounced]);

  return {
    selectedElements,
    selectionType,
    mixed,
    canEdit,
    lockedState,
    applyToSelection,
    commitPending,
    cancelPending,
    isInteractingRef,
  };
};

export default usePropertiesModel;
